'use client';

import { c as _c } from "react/compiler-runtime";
import type { Money } from '@repo/shopify';
import { Gift, Plus, X } from 'lucide-react';
import Image from 'next/image';
import { type ReactNode, useId } from 'react';
import type { GiftSelection } from '@/components/product/gift-variant-modal';
import { PurchaseTrustList } from '@/components/product/purchase-trust-list';
import { ShopifyImage } from '@/components/ui/shopify-image';
import type { HydrateBundleFlavor } from '@/lib/insider-bundle-config';
import { formatMoney } from '@/lib/money';
import { getSellingPlanFrequencyOptionLabel } from '@/lib/selling-plans';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';
import { useHydrateBundleAdd } from './use-hydrate-bundle-add';
import type { HydrateBundleState } from './use-hydrate-bundle-state';

/**
 * One pick slot: a paid box ("Box 1") or the free bonus box. Filled slots keep
 * the artwork clean and carry the flavor name + remove control in a footer
 * strip instead of overlaying an ✕ chip on the pack image.
 */
function BundleSlot(t0) {
  const $ = _c(30);
  const {
    flavor,
    slotLabel,
    isBonus,
    onClear
  } = t0;
  if (!flavor) {
    const t1 = `flex aspect-[3/4] flex-col items-center justify-center rounded border border-dashed p-1 text-center ${isBonus ? "border-brand-accent bg-brand-accent/5" : "border-neutral-300"}`;
    let t2;
    if ($[0] !== isBonus) {
      t2 = isBonus && <Gift className="mb-1 h-4 w-4 text-brand-accent" aria-hidden="true" />;
      $[0] = isBonus;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const t3 = `text-[10px] font-bold uppercase tracking-wide ${isBonus ? "text-brand-accent" : "text-neutral-400"}`;
    let t4;
    if ($[2] !== slotLabel || $[3] !== t3) {
      t4 = <span className={t3}>{slotLabel}</span>;
      $[2] = slotLabel;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== isBonus) {
      t5 = isBonus && <span className="mt-0.5 text-[9px] font-bold uppercase text-brand-accent">Free</span>;
      $[5] = isBonus;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t1 || $[8] !== t2 || $[9] !== t4 || $[10] !== t5) {
      t6 = <div className={t1}>{t2}{t4}{t5}</div>;
      $[7] = t1;
      $[8] = t2;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t1;
  if ($[12] !== flavor.image || $[13] !== flavor.label) {
    t1 = flavor.image ? <ShopifyImage src={flavor.image.url} alt={flavor.image.altText || flavor.label} fill={true} sizes="120px" className="object-cover" /> : <span className="flex h-full items-center justify-center text-[10px] font-bold uppercase">{flavor.label}</span>;
    $[12] = flavor.image;
    $[13] = flavor.label;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== isBonus) {
    t2 = isBonus && <span className="absolute left-1 top-1 rounded-sm bg-brand-accent px-1 py-0.5 text-[8px] font-bold uppercase leading-none tracking-wide text-white">Free</span>;
    $[15] = isBonus;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== t1 || $[18] !== t2) {
    t3 = <span className="relative min-h-0 flex-1 bg-neutral-50">{t1}{t2}</span>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = `Remove ${flavor.label} from ${slotLabel}`;
  let t5;
  if ($[20] !== flavor.label) {
    t5 = <span className="min-w-0 truncate text-[9px] font-bold uppercase tracking-wide text-neutral-800">{flavor.label}</span>;
    $[20] = flavor.label;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <X className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" aria-hidden="true" />;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== onClear || $[24] !== t4 || $[25] !== t5) {
    t7 = <button type="button" className="flex w-full items-center justify-between gap-1 border-t border-neutral-200 px-1.5 py-1.5 text-left transition-colors hover:bg-neutral-100" onClick={onClear} aria-label={t4}>{t5}{t6}</button>;
    $[23] = onClear;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t3 || $[28] !== t7) {
    t8 = <div className="flex aspect-[3/4] flex-col overflow-hidden rounded border border-neutral-900 bg-white">{t3}{t7}</div>;
    $[27] = t3;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}

/**
 * The Insider Bundle buy box (LMNT-style): pick-your-flavor slots (paid boxes
 * + a free bonus box), the flavor grid, then the mode tabs (passed in as
 * `modeTabs` so they sit directly above the purchase options), Subscribe &
 * Save vs one-time totals, and a delivery-frequency selector with a supply
 * hint. All prices shown here are display-only — the Crew Pack kit product's
 * variant price and its Recharge selling plans do the real pricing at
 * checkout. State lives in useHydrateBundleState (lifted so the buy-box price
 * row mirrors it).
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: bundle rendering combines slot picking, frequency selection, and purchase-mode pricing (same shape as ProductInfo)
export function HydrateBundlePanel(t0) {
  const $ = _c(109);
  const {
    state,
    subscribeSaveConfig,
    moneyLocale,
    onGiftSelections,
    modeTabs
  } = t0;
  const radioGroupName = useId();
  const {
    config,
    flavors,
    kitProduct,
    byHandle,
    slots,
    fillSlot,
    clearSlot,
    remainingCount,
    hasUnavailableSelection,
    showSubscribe,
    subscribeSelected,
    setSubscribeSelected,
    frequencyOptions,
    selectedFrequency,
    setSelectedFrequencyPhrase,
    pricing,
    canAdd
  } = state;
  let t1;
  if ($[0] !== flavors || $[1] !== kitProduct || $[2] !== onGiftSelections || $[3] !== subscribeSaveConfig) {
    t1 = {
      flavors,
      kitProduct,
      subscribeSaveConfig,
      onGiftSelections
    };
    $[0] = flavors;
    $[1] = kitProduct;
    $[2] = onGiftSelections;
    $[3] = subscribeSaveConfig;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    addBundle,
    isAdding
  } = useHydrateBundleAdd(t1);
  let t2;
  if ($[5] !== addBundle || $[6] !== canAdd || $[7] !== isAdding || $[8] !== selectedFrequency || $[9] !== slots) {
    t2 = () => {
      if (!canAdd || isAdding) {
        return;
      }
      addBundle({
        slots,
        frequency: selectedFrequency
      });
    };
    $[5] = addBundle;
    $[6] = canAdd;
    $[7] = isAdding;
    $[8] = selectedFrequency;
    $[9] = slots;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleAdd = t2;
  let t3;
  if ($[11] !== canAdd || $[12] !== config.subscribeCtaLabel || $[13] !== remainingCount || $[14] !== subscribeSaveConfig.subscribeCtaLabel || $[15] !== subscribeSelected) {
    t3 = !canAdd ? remainingCount > 0 ? `PICK ${remainingCount} MORE FLAVOR${remainingCount === 1 ? "" : "S"}` : "UNAVAILABLE" : subscribeSelected ? (config.subscribeCtaLabel || subscribeSaveConfig.subscribeCtaLabel).toUpperCase() : "ADD TO CART";
    $[11] = canAdd;
    $[12] = config.subscribeCtaLabel;
    $[13] = remainingCount;
    $[14] = subscribeSaveConfig.subscribeCtaLabel;
    $[15] = subscribeSelected;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const ctaLabel = t3;
  const savePercent = pricing?.subscribePercentOff ?? null;
  const ribbonText = savePercent !== null ? `${subscribeSaveConfig.popularBadge} · Save ${savePercent}%` : subscribeSaveConfig.popularBadge;
  let t4;
  if ($[17] !== pricing) {
    t4 = total => pricing && total && Number.parseFloat(pricing.retailTotal.amount) > Number.parseFloat(total.amount) ? pricing.retailTotal : null;
    $[17] = pricing;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const compareAgainstRetail = t4;
  const t5 = pricing?.subscribeTotal ?? null;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[19] !== byHandle || $[20] !== clearSlot || $[21] !== compareAgainstRetail || $[22] !== config.bonusSlotLabel || $[23] !== config.boxCount || $[24] !== config.description || $[25] !== fillSlot || $[26] !== flavors || $[27] !== frequencyOptions || $[28] !== hasUnavailableSelection || $[29] !== modeTabs || $[30] !== moneyLocale || $[31] !== pricing || $[32] !== radioGroupName || $[33] !== remainingCount || $[34] !== ribbonText || $[35] !== selectedFrequency?.phrase || $[36] !== setSelectedFrequencyPhrase || $[37] !== setSubscribeSelected || $[38] !== showSubscribe || $[39] !== slots || $[40] !== subscribeSaveConfig.gifts || $[41] !== subscribeSaveConfig.giftsHeading || $[42] !== subscribeSaveConfig.oneTimeLabel || $[43] !== subscribeSaveConfig.planMeta || $[44] !== subscribeSaveConfig.subscribeLabel || $[45] !== subscribeSaveConfig.title || $[46] !== subscribeSelected || $[47] !== t5) {
    const subscribeCompare = compareAgainstRetail(t5);
    const oneTimeCompare = compareAgainstRetail(pricing?.oneTimeTotal ?? null);
    t6 = "space-y-5";
    if ($[55] !== config.description) {
      t7 = config.description && <p className="text-[13px] leading-relaxed text-neutral-600">{config.description}</p>;
      $[55] = config.description;
      $[56] = t7;
    } else {
      t7 = $[56];
    }
    t8 = modeTabs;
    const t13 = `repeat(${slots.length}, minmax(0, 1fr))`;
    let t14;
    if ($[57] !== t13) {
      t14 = {
        gridTemplateColumns: t13
      };
      $[57] = t13;
      $[58] = t14;
    } else {
      t14 = $[58];
    }
    let t15;
    if ($[59] !== byHandle || $[60] !== clearSlot || $[61] !== config.bonusSlotLabel || $[62] !== config.boxCount || $[63] !== slots) {
      let t16;
      if ($[65] !== byHandle || $[66] !== clearSlot || $[67] !== config.bonusSlotLabel || $[68] !== config.boxCount) {
        t16 = (handle, index) => {
          const isBonus = index >= config.boxCount;
          return <BundleSlot key={index} flavor={handle ? byHandle.get(handle) : undefined} slotLabel={isBonus ? config.bonusSlotLabel : `Bag ${index + 1}`} isBonus={isBonus} onClear={() => clearSlot(index)} />;
        };
        $[65] = byHandle;
        $[66] = clearSlot;
        $[67] = config.bonusSlotLabel;
        $[68] = config.boxCount;
        $[69] = t16;
      } else {
        t16 = $[69];
      }
      t15 = slots.map(t16);
      $[59] = byHandle;
      $[60] = clearSlot;
      $[61] = config.bonusSlotLabel;
      $[62] = config.boxCount;
      $[63] = slots;
      $[64] = t15;
    } else {
      t15 = $[64];
    }
    if ($[70] !== t14 || $[71] !== t15) {
      t9 = <div className="grid gap-2" style={t14}>{t15}</div>;
      $[70] = t14;
      $[71] = t15;
      $[72] = t9;
    } else {
      t9 = $[72];
    }
    let t16;
    if ($[73] !== fillSlot || $[74] !== flavors || $[75] !== remainingCount || $[76] !== slots) {
      let t17;
      if ($[78] !== fillSlot || $[79] !== remainingCount || $[80] !== slots) {
        t17 = flavor => {
          const pickedCount = slots.filter(slot => slot === flavor.handle).length;
          return <button key={flavor.handle} type="button" disabled={flavor.availableForSale === false || remainingCount === 0} className="group relative flex flex-col items-center rounded border border-neutral-200 p-1.5 pb-2 transition-colors enabled:hover:border-neutral-900 disabled:cursor-not-allowed disabled:opacity-45" onClick={() => fillSlot(flavor.handle)}>{pickedCount > 0 && <span className="absolute -right-1.5 -top-1.5 z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-neutral-900 px-1 text-[10px] font-bold leading-none text-white">×{pickedCount}</span>}<span className="relative aspect-square w-full overflow-hidden rounded-[3px] bg-neutral-50">{flavor.image && <ShopifyImage src={flavor.image.url} alt={flavor.image.altText || flavor.label} fill={true} sizes="120px" className="object-contain" />}</span><span className="mt-1.5 flex min-h-[26px] items-center justify-center gap-0.5"><Plus aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-neutral-400 transition-colors group-enabled:group-hover:text-neutral-900" /><span className="text-center text-[10px] font-bold uppercase leading-tight tracking-wide text-neutral-800">{flavor.label}</span></span>{flavor.availableForSale === false && <span className="text-[9px] font-semibold uppercase text-neutral-400">Sold out</span>}</button>;
        };
        $[78] = fillSlot;
        $[79] = remainingCount;
        $[80] = slots;
        $[81] = t17;
      } else {
        t17 = $[81];
      }
      t16 = flavors.map(t17);
      $[73] = fillSlot;
      $[74] = flavors;
      $[75] = remainingCount;
      $[76] = slots;
      $[77] = t16;
    } else {
      t16 = $[77];
    }
    if ($[82] !== t16) {
      t10 = <div className="grid grid-cols-4 gap-2">{t16}</div>;
      $[82] = t16;
      $[83] = t10;
    } else {
      t10 = $[83];
    }
    if ($[84] !== hasUnavailableSelection) {
      t11 = hasUnavailableSelection && <p className="text-[11px] font-semibold text-brand-accent">A picked flavor just sold out — swap it for another to continue.</p>;
      $[84] = hasUnavailableSelection;
      $[85] = t11;
    } else {
      t11 = $[85];
    }
    t12 = pricing && <fieldset><legend className="mb-3 text-[13px] font-bold uppercase tracking-wide text-neutral-900">{subscribeSaveConfig.title}</legend>{showSubscribe && <div className={`relative cursor-pointer rounded border transition-colors ${subscribeSelected ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-300 hover:border-neutral-400"}`} onClick={event => {
        if (event.target instanceof Element && event.target.closest("label")) {
          return;
        }
        setSubscribeSelected(true);
      }}><span className={`absolute -top-[11px] left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-[0.06em] ${subscribeSelected ? "bg-brand-accent text-white" : "bg-neutral-200 text-neutral-600"}`}>{ribbonText}</span><label className="flex cursor-pointer items-center justify-between gap-3 px-3 pt-4 pb-2.5"><input type="radio" name={radioGroupName} className="sr-only" checked={subscribeSelected} onChange={() => setSubscribeSelected(true)} /><span><span className="block text-[13px] font-bold uppercase tracking-wide text-neutral-900">{subscribeSaveConfig.subscribeLabel}</span>{subscribeSaveConfig.planMeta && <span className="block text-[11px] text-neutral-500">{subscribeSaveConfig.planMeta}</span>}</span>{pricing.subscribeTotal && <span className="flex flex-col items-end">{subscribeCompare && <span className="text-[11px] leading-tight text-neutral-400 line-through">{formatMoney(subscribeCompare, moneyLocale)}</span>}<span className="text-base font-black leading-tight text-neutral-900">{formatMoney(pricing.subscribeTotal, moneyLocale)}</span></span>}</label>{subscribeSelected && frequencyOptions.length > 1 && <div className="border-t border-neutral-200 px-3 py-1.5"><label className="flex cursor-pointer items-center justify-between gap-3 text-[11px] leading-none text-neutral-500"><span className="flex-shrink-0 leading-none">Deliver every</span><select className="w-auto min-w-0 cursor-pointer appearance-none border-0 bg-transparent bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2210%22%20height%3D%2210%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23525252%22%20stroke-width%3D%222.5%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_0_center] bg-no-repeat p-0 pr-3.5 text-[11px] font-semibold leading-none text-neutral-800 underline decoration-neutral-300 underline-offset-2 transition-colors hover:decoration-neutral-800 focus:outline-none" value={selectedFrequency?.phrase ?? ""} onChange={event_0 => setSelectedFrequencyPhrase(event_0.target.value)}>{frequencyOptions.map(_temp)}</select></label></div>}{subscribeSelected && subscribeSaveConfig.gifts.length > 0 && <div className="border-t border-neutral-200 px-3 py-2.5"><p className="text-[11px] font-bold uppercase tracking-wider text-brand-accent">{subscribeSaveConfig.giftsHeading}</p><ul className="mt-2 space-y-2">{subscribeSaveConfig.gifts.map(_temp2)}</ul></div>}</div>}<label className={`mt-2 flex cursor-pointer items-center justify-between gap-3 rounded border px-3 py-2.5 transition-colors ${!subscribeSelected ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-300 hover:border-neutral-400"}`}><input type="radio" name={radioGroupName} className="sr-only" checked={!subscribeSelected} onChange={() => setSubscribeSelected(false)} /><span className="text-[13px] font-medium text-neutral-700">{subscribeSaveConfig.oneTimeLabel}</span><span className="flex items-baseline gap-1.5">{oneTimeCompare && <span className="text-[11px] text-neutral-400 line-through">{formatMoney(oneTimeCompare, moneyLocale)}</span>}<span className="text-[13px] font-bold text-neutral-900">{formatMoney(pricing.oneTimeTotal, moneyLocale)}</span></span></label></fieldset>;
    $[19] = byHandle;
    $[20] = clearSlot;
    $[21] = compareAgainstRetail;
    $[22] = config.bonusSlotLabel;
    $[23] = config.boxCount;
    $[24] = config.description;
    $[25] = fillSlot;
    $[26] = flavors;
    $[27] = frequencyOptions;
    $[28] = hasUnavailableSelection;
    $[29] = modeTabs;
    $[30] = moneyLocale;
    $[31] = pricing;
    $[32] = radioGroupName;
    $[33] = remainingCount;
    $[34] = ribbonText;
    $[35] = selectedFrequency?.phrase;
    $[36] = setSelectedFrequencyPhrase;
    $[37] = setSubscribeSelected;
    $[38] = showSubscribe;
    $[39] = slots;
    $[40] = subscribeSaveConfig.gifts;
    $[41] = subscribeSaveConfig.giftsHeading;
    $[42] = subscribeSaveConfig.oneTimeLabel;
    $[43] = subscribeSaveConfig.planMeta;
    $[44] = subscribeSaveConfig.subscribeLabel;
    $[45] = subscribeSaveConfig.title;
    $[46] = subscribeSelected;
    $[47] = t5;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
  } else {
    t10 = $[48];
    t11 = $[49];
    t12 = $[50];
    t6 = $[51];
    t7 = $[52];
    t8 = $[53];
    t9 = $[54];
  }
  const t13 = `w-full rounded py-4 text-base font-bold uppercase tracking-[0.08em] transition-all ${canAdd ? "bg-brand-accent text-white hover:brightness-110" : "cursor-not-allowed bg-neutral-300 text-neutral-500"}`;
  const t14 = !canAdd || isAdding;
  let t15;
  if ($[86] !== ctaLabel || $[87] !== isAdding) {
    t15 = isAdding ? <span className="flex items-center justify-center"><svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true"><title>Loading</title><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg></span> : ctaLabel;
    $[86] = ctaLabel;
    $[87] = isAdding;
    $[88] = t15;
  } else {
    t15 = $[88];
  }
  let t16;
  if ($[89] !== handleAdd || $[90] !== t13 || $[91] !== t14 || $[92] !== t15) {
    t16 = <button type="button" className={t13} onClick={handleAdd} disabled={t14}>{t15}</button>;
    $[89] = handleAdd;
    $[90] = t13;
    $[91] = t14;
    $[92] = t15;
    $[93] = t16;
  } else {
    t16 = $[93];
  }
  let t17;
  if ($[94] !== subscribeSaveConfig.benefits || $[95] !== subscribeSelected) {
    t17 = subscribeSelected && <PurchaseTrustList items={subscribeSaveConfig.benefits.map(_temp3)} subtext={null} />;
    $[94] = subscribeSaveConfig.benefits;
    $[95] = subscribeSelected;
    $[96] = t17;
  } else {
    t17 = $[96];
  }
  let t18;
  if ($[97] !== t16 || $[98] !== t17) {
    t18 = <div className="space-y-3">{t16}{t17}</div>;
    $[97] = t16;
    $[98] = t17;
    $[99] = t18;
  } else {
    t18 = $[99];
  }
  let t19;
  if ($[100] !== t10 || $[101] !== t11 || $[102] !== t12 || $[103] !== t18 || $[104] !== t6 || $[105] !== t7 || $[106] !== t8 || $[107] !== t9) {
    t19 = <div className={t6}>{t7}{t8}{t9}{t10}{t11}{t12}{t18}</div>;
    $[100] = t10;
    $[101] = t11;
    $[102] = t12;
    $[103] = t18;
    $[104] = t6;
    $[105] = t7;
    $[106] = t8;
    $[107] = t9;
    $[108] = t19;
  } else {
    t19 = $[108];
  }
  return t19;
}
function _temp3(benefit) {
  return benefit.text;
}
function _temp2(gift) {
  return <li key={gift.title} className="flex items-center gap-2.5"><span className="relative h-8 w-8 flex-shrink-0 overflow-hidden rounded bg-neutral-100">{gift.imageUrl && <Image src={gift.imageUrl} alt={gift.imageAlt} fill={true} sizes="32px" style={{
        objectFit: "cover"
      }} />}</span><span className="min-w-0 flex-1 truncate text-[12px] font-bold uppercase tracking-wide text-neutral-900">{gift.title}</span><span className="flex flex-shrink-0 items-center gap-1.5">{gift.value && <span className="text-[11px] text-neutral-400 line-through">{gift.value}</span>}<span className="rounded bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Free</span></span></li>;
}
function _temp(option) {
  return <option key={option.phrase} value={option.phrase}>{getSellingPlanFrequencyOptionLabel(option.plan)}</option>;
}